import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTTable from '../common/Table';
import RTSpinner from '../common/RTSpinner';
import tenantStorage from '../../utils/tenantStorage';
import RTButton from '../common/RTButton';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { apiGet } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';
import './JobReportList.css';

const JobReportList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  
  // Get first day of current month and today's date
  const getDefaultDates = () => {
    const today = new Date();
    const firstDayCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      start_date: firstDayCurrentMonth,
      end_date: today
    };
  };
  
  const [filters, setFilters] = useState({
    start_date: getDefaultDates().start_date,
    end_date: getDefaultDates().end_date,
    job_name: '',
    job_type: '',
    status: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({
    start_date: getDefaultDates().start_date,
    end_date: getDefaultDates().end_date,
    job_name: '',
    job_type: '',
    status: ''
  });

  const fetchJobs = async (filterParams = appliedFilters, abortController = null) => {
    setLoading(true);
    try {
      const params = {};
      if (filterParams.status) params.status = filterParams.status;
      if (filterParams.job_name) params.job_name = filterParams.job_name;
      if (filterParams.job_type) params.job_type = filterParams.job_type;
      
      // Format dates to YYYY-MM-DD in local timezone
      if (filterParams.start_date) {
        const year = filterParams.start_date.getFullYear();
        const month = String(filterParams.start_date.getMonth() + 1).padStart(2, '0');
        const day = String(filterParams.start_date.getDate()).padStart(2, '0');
        params.start_date = `${year}-${month}-${day}`;
      }
      if (filterParams.end_date) {
        const year = filterParams.end_date.getFullYear();
        const month = String(filterParams.end_date.getMonth() + 1).padStart(2, '0');
        const day = String(filterParams.end_date.getDate()).padStart(2, '0');
        params.end_date = `${year}-${month}-${day}`;
      }
      
      console.log('Applied filters:', filterParams);
      console.log('API params:', params);
      
      const token = tenantStorage.getItem('token');
      const response = await apiGet('/jobs/', { 
        params: params,
        headers: { 'Authorization': `Bearer ${token}` },
        signal: abortController?.signal
      });
      
      const apiResponse = response.data;
      if (apiResponse && Array.isArray(apiResponse)) {
        setJobs(apiResponse);
        toastSuccess(`Loaded ${apiResponse.length} job records`);
      } else {
        setJobs([]);
        toastError('No jobs found for the selected filters.');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error loading jobs:', err);
        toastError('Failed to fetch jobs');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs(appliedFilters);
    
    // eslint-disable-next-line
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
    fetchJobs(filters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      start_date: getDefaultDates().start_date,
      end_date: getDefaultDates().end_date,
      job_name: '',
      job_type: '',
      status: ''
    };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    fetchJobs(defaultFilters);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationMs = endDate - startDate;
    const durationMinutes = Math.floor(durationMs / 60000);
    const durationSeconds = Math.floor((durationMs % 60000) / 1000);
    return `${durationMinutes}m ${durationSeconds}s`;
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = 'job-status-badge';
    
    if (status === 'completed') {
      return `${baseClass} job-status-completed`;
    } else if (status === 'failed') {
      return `${baseClass} job-status-failed`;
    } else if (status === 'processing') {
      return `${baseClass} job-status-processing`;
    } else {
      return `${baseClass} job-status-pending`;
    }
  };

  const formatJobType = (jobType) => {
    if (!jobType) return '';
    // Convert snake_case to Title Case
    return jobType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const viewJobDetails = (job) => {
    navigate(`/reports/jobs/${job.job_id}`);
  };

  const columns = [
    { 
      Header: 'Job Name', 
      accessor: 'job_name',
      Cell: ({ row }) => (
        <span 
          className="job-name-link" 
          onClick={() => viewJobDetails(row)}
          style={{ cursor: 'pointer' }}
        >
          {row.job_name}
        </span>
      )
    },
    { 
      Header: 'Job Type', 
      accessor: 'job_type',
      Cell: ({ value }) => formatJobType(value)
    },
    { 
      Header: 'Started At', 
      accessor: 'job_start_time',
      Cell: ({ value }) => formatTimestamp(value)
    },
    { 
      Header: 'Duration', 
      accessor: 'duration',
      Cell: ({ row }) => formatDuration(row.job_start_time, row.job_end_time)
    },
    { 
      Header: 'Total', 
      accessor: 'total_records'
    },
    { 
      Header: 'Processed', 
      accessor: 'processed_records',
      Cell: ({ value, row }) => {
        if (row.status === 'processing') {
          const progress = row.total_records > 0 
            ? Math.round((value / row.total_records) * 100) 
            : 0;
          return `${value} (${progress}%)`;
        }
        return value;
      }
    },
    { 
      Header: 'Success', 
      accessor: 'success_records',
      Cell: ({ value, row }) => {
        const successRate = row.total_records > 0 
          ? Math.round((value / row.total_records) * 100) 
          : 0;
        return `${value} (${successRate}%)`;
      }
    },
    { 
      Header: 'Failed', 
      accessor: 'failed_records'
    },
    { 
      Header: 'Status', 
      accessor: 'status',
      Cell: ({ value }) => (
        <span className={getStatusBadgeClass(value)}>
          {value || 'Unknown'}
        </span>
      )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <RTButton
          type="link"
          size="sm"
          onClick={() => viewJobDetails(row)}
        >
          View Details
        </RTButton>
      )
    }
  ];

  // Jobs list view
  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">Background Jobs</h2>
          <div className='d-flex gap-2'>
            <RTButton
              variant="outline-primary"
              size="sm"
              onClick={() => navigate('/reports')}
            >
              &#8592; Back
            </RTButton>
            <RTButton
              variant="primary"
              size="sm"
              onClick={() => fetchJobs(appliedFilters)}
              disabled={loading}
            >
              Refresh
            </RTButton>
          </div>
        </div>

        {/* Filters Section */}
        <div className="rt-section" style={{ flex: 'none' }}>
          <div className="row mb-3">
            <div className="col-md-2">
              <label className="form-label">Start Date</label>
              <DatePicker
                selected={filters.start_date}
                onChange={(date) => handleFilterChange('start_date', date)}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                placeholderText="DD/MM/YYYY"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                autoComplete="off"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">End Date</label>
              <DatePicker
                selected={filters.end_date}
                onChange={(date) => handleFilterChange('end_date', date)}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                placeholderText="DD/MM/YYYY"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                autoComplete="off"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Job Name</label>
              <input
                type="text"
                className="form-control"
                value={filters.job_name || ''}
                onChange={(e) => handleFilterChange('job_name', e.target.value)}
                placeholder="Search job name..."
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Job Type</label>
              <select
                className="form-select"
                value={filters.job_type || ''}
                onChange={(e) => handleFilterChange('job_type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="send_invoice">Send Invoice</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <RTButton
                variant="primary"
                onClick={applyFilters}
                className="me-2"
                disabled={loading}
              >
                Apply
              </RTButton>
              <RTButton
                variant="outline-secondary"
                onClick={resetFilters}
                disabled={loading}
              >
                Reset
              </RTButton>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="rt-section">
          {loading ? (
            <div className="job-spinner-container">
              <RTSpinner message='Loading jobs...' />
            </div>
          ) : (
            <RTTable
              columns={columns}
              data={jobs}
              exportable={false}
              emptyMessage="No jobs found for the selected criteria"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default JobReportList;
